import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Printer } from 'lucide-react';

import { apiErrorMessage } from '../../lib/api';
import { economicsAPI } from '../../lib/economicsApi';
import { formatDate, formatMois, formatNombre, francisaTexte } from '../../lib/formats';
import { libelle, referenceLisible } from '../../lib/libelles';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { EmptyState } from '../ui/empty-state';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { LoadingState } from '../ui/loading-state';
import { StatsCompactes } from '../ui/stats-compactes';
import { toast } from '../ui/sonner';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';

/**
 * Onglet « Rentabilité » de la fiche camion — le compte d'exploitation du mois,
 * présenté comme le classeur Excel de l'exploitant.
 *
 * Une ligne par rotation et par fait de charge daté; les colonnes sont celles du
 * classeur: Date | Tracteur | Citerne | Rotation | Recettes (Avance, Paiement, Fact.
 * transport) | Total transport | Charges d'exploitation (Salaire, Assurance, Frais de
 * route, Autres) | Total exploitation | Charge de maintenance (Entretien et réparation,
 * Pièces détachées, Renouv. pièces adm.) | Total maintenance | Coût total des charges |
 * Résultat | Observations.
 *
 * Deux colonnes n'apparaissent que lorsqu'elles servent — « Loyer » (camion pris en
 * location) et « Amortissement » (camion propre dont le prix d'achat est saisi) —
 * pour que la lecture « total = somme des colonnes » reste vraie à l'écran.
 *
 * Avance et Paiement sont des sommes déjà encaissées, données à titre d'information:
 * elles n'entrent dans aucun total. Un montant estimé est signalé « prévu ».
 *
 * Étape du parcours: encaisser et mesurer ce que rapporte chaque camion. Réservé à
 * la direction (`canSeeMoney`).
 */

const nombre = (valeur) => {
  const n = Number(valeur);
  return Number.isFinite(n) ? n : 0;
};

/** Un montant à zéro reste vide, comme dans le classeur. */
const montantCellule = (valeur) => {
  const n = nombre(valeur);
  return n === 0 ? '' : formatNombre(n);
};

const deuxChiffres = (n) => String(n).padStart(2, '0');

const moisSuivant = ({ year, month }, pas) => {
  const index = year * 12 + (month - 1) + pas;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
};

const moisCourant = () => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
};

const estApresMoisCourant = ({ year, month }) => {
  const now = moisCourant();
  return year * 12 + month > now.year * 12 + now.month;
};

/** « le mois d'octobre 2026 » → « octobre 2026 ». Le serveur donne déjà le libellé. */
const libelleMois = (periode, choix) =>
  periode?.label || formatMois(`${choix.year}-${deuxChiffres(choix.month)}`);

const ligneEstPrevue = (ligne) => Boolean(ligne?.estimated);

/** Texte de la colonne Observations: ce que la ligne est, puis les constats. */
const observationsDeLigne = (ligne) => {
  const textes = [];
  if (ligne.label) textes.push(francisaTexte(ligne.label));
  (Array.isArray(ligne.notes) ? ligne.notes : []).forEach((note) => {
    if (note) textes.push(francisaTexte(note));
  });
  return textes;
};

const trierLignes = (lignes) =>
  [...(Array.isArray(lignes) ? lignes : [])]
    .map((ligne, position) => ({ ligne, position }))
    .sort((a, b) => {
      const da = String(a.ligne.date || '');
      const db = String(b.ligne.date || '');
      if (da !== db) return da < db ? -1 : 1;
      // La rotation d'abord, puis les charges du même jour.
      if (a.ligne.kind !== b.ligne.kind) return a.ligne.kind === 'ROTATION' ? -1 : 1;
      return a.position - b.position;
    })
    .map(({ ligne }) => ligne);

const tonDuResultat = (valeur) => {
  const n = nombre(valeur);
  if (n > 0) return 'text-success';
  if (n < 0) return 'text-destructive';
  return 'text-foreground';
};

// --- Le tableau, calqué sur le classeur -----------------------------------------------

const TH = 'whitespace-nowrap border border-border/60 px-2 py-1.5 text-center text-xs font-semibold text-muted-foreground';
const TD = 'whitespace-nowrap border border-border/40 px-2 py-1.5 text-right tabular-nums';
const TD_SOUS_TOTAL = `${TD} bg-muted/40 font-medium`;

export function CompteExploitationTable({ lignes, totaux }) {
  const rangees = trierLignes(lignes);
  const afficherLoyer =
    nombre(totaux?.rent) !== 0 || rangees.some((ligne) => nombre(ligne.rent) !== 0);
  const afficherAmortissement =
    nombre(totaux?.depreciation) !== 0 || rangees.some((ligne) => nombre(ligne.depreciation) !== 0);
  const colonnesExploitation = 4 + (afficherLoyer ? 1 : 0);

  return (
    <div className="space-y-2">
      <div className="table-scroll rounded-lg border border-border bg-card">
        <table className="w-full border-collapse text-sm" data-testid="compte-exploitation">
          <caption className="sr-only">Compte d'exploitation du camion pour le mois</caption>
          <thead className="bg-muted/50">
            <tr>
              <th rowSpan={2} className={TH}>Date</th>
              <th rowSpan={2} className={TH}>Tracteur</th>
              <th rowSpan={2} className={TH}>Citerne</th>
              <th rowSpan={2} className={TH}>Rotation</th>
              <th colSpan={3} className={TH}>Recettes</th>
              <th rowSpan={2} className={`${TH} bg-muted/70 text-foreground`}>Total transport</th>
              <th colSpan={colonnesExploitation} className={TH}>Charges d'exploitation</th>
              <th rowSpan={2} className={`${TH} bg-muted/70 text-foreground`}>Total exploitation</th>
              <th colSpan={3} className={TH}>Charge de maintenance</th>
              <th rowSpan={2} className={`${TH} bg-muted/70 text-foreground`}>Total maintenance</th>
              {afficherAmortissement && <th rowSpan={2} className={TH}>Amortissement</th>}
              <th rowSpan={2} className={`${TH} bg-muted/70 text-foreground`}>Coût total des charges</th>
              <th rowSpan={2} className={`${TH} bg-muted/70 text-foreground`}>Résultat</th>
              <th rowSpan={2} className={TH}>Observations</th>
            </tr>
            <tr>
              <th
                className={`${TH} font-normal italic`}
                title="Somme déjà encaissée avant la facture, à titre d'information: elle n'entre dans aucun total."
              >
                Avance
              </th>
              <th
                className={`${TH} font-normal italic`}
                title="Somme déjà encaissée sur la facture, à titre d'information: elle n'entre dans aucun total."
              >
                Paiement
              </th>
              <th className={TH}>Fact. transport</th>
              <th className={TH}>Salaire</th>
              <th className={TH}>Assurance</th>
              <th className={TH}>Frais de route</th>
              <th className={TH}>Autres</th>
              {afficherLoyer && <th className={TH}>Fret</th>}
              <th className={TH}>Entretien et réparation</th>
              <th className={TH}>Pièces détachées</th>
              <th className={TH}>Renouv. pièces adm.</th>
            </tr>
          </thead>
          <tbody>
            {rangees.map((ligne, index) => {
              const prevue = ligneEstPrevue(ligne);
              const observations = observationsDeLigne(ligne);
              const convoi = referenceLisible(ligne.trip_ref, '');
              return (
                <tr
                  key={`${ligne.date}-${ligne.kind}-${index}`}
                  className={`hover:bg-muted/30 ${prevue ? 'italic text-muted-foreground' : ''}`}
                  data-testid="ligne-exploitation"
                >
                  <td className={`${TD} text-left`}>{formatDate(ligne.date)}</td>
                  <td className={`${TD} text-left`}>
                    {ligne.tractor_plate || '—'}
                    {ligne.driver_name && (
                      <span className="block text-[11px] font-normal text-muted-foreground">{ligne.driver_name}</span>
                    )}
                  </td>
                  <td className={`${TD} text-left`}>{ligne.tank_plate || ''}</td>
                  <td className={`${TD} text-center`}>{nombre(ligne.rotations) > 0 ? formatNombre(ligne.rotations) : ''}</td>
                  <td className={`${TD} text-muted-foreground`}>{montantCellule(ligne.advance_received)}</td>
                  <td className={`${TD} text-muted-foreground`}>{montantCellule(ligne.payment_received)}</td>
                  <td className={TD}>{montantCellule(ligne.revenue)}</td>
                  <td className={TD_SOUS_TOTAL}>{montantCellule(ligne.total_revenue)}</td>
                  <td className={TD}>{montantCellule(ligne.salary)}</td>
                  <td className={TD}>{montantCellule(ligne.insurance)}</td>
                  <td className={TD}>{montantCellule(ligne.road_fees)}</td>
                  <td className={TD}>{montantCellule(ligne.other_fees)}</td>
                  {afficherLoyer && <td className={TD}>{montantCellule(ligne.rent)}</td>}
                  <td className={TD_SOUS_TOTAL}>{montantCellule(ligne.total_operating)}</td>
                  <td className={TD}>{montantCellule(ligne.repair)}</td>
                  <td className={TD}>{montantCellule(ligne.parts)}</td>
                  <td className={TD}>{montantCellule(ligne.admin_papers)}</td>
                  <td className={TD_SOUS_TOTAL}>{montantCellule(ligne.total_maintenance)}</td>
                  {afficherAmortissement && <td className={TD}>{montantCellule(ligne.depreciation)}</td>}
                  <td className={TD_SOUS_TOTAL}>{montantCellule(ligne.total_cost)}</td>
                  <td className={`${TD} font-semibold ${tonDuResultat(ligne.result)}`}>
                    {nombre(ligne.result) === 0 && nombre(ligne.total_cost) === 0 && nombre(ligne.total_revenue) === 0
                      ? ''
                      : formatNombre(ligne.result)}
                  </td>
                  <td className={`${TD} min-w-[14rem] max-w-[22rem] whitespace-normal text-left text-xs`}>
                    {observations.join(' · ')}
                    {convoi && (
                      <span className="block text-[11px] text-muted-foreground">{convoi}</span>
                    )}
                    {prevue && (
                      <span className="ml-1 rounded bg-flame/10 px-1 text-[11px] not-italic text-flame-ink">prévu</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-muted/60 font-semibold" data-testid="total-exploitation">
              <td className={`${TD} text-left`} colSpan={3}>Total du mois</td>
              <td className={`${TD} text-center`}>{formatNombre(totaux?.rotations ?? 0)}</td>
              <td className={`${TD} text-muted-foreground`}>{montantCellule(totaux?.advance_received)}</td>
              <td className={`${TD} text-muted-foreground`}>{montantCellule(totaux?.payment_received)}</td>
              <td className={TD}>{formatNombre(totaux?.revenue ?? 0)}</td>
              <td className={TD}>{formatNombre(totaux?.total_revenue ?? 0)}</td>
              <td className={TD}>{formatNombre(totaux?.salary ?? 0)}</td>
              <td className={TD}>{formatNombre(totaux?.insurance ?? 0)}</td>
              <td className={TD}>{formatNombre(totaux?.road_fees ?? 0)}</td>
              <td className={TD}>{formatNombre(totaux?.other_fees ?? 0)}</td>
              {afficherLoyer && <td className={TD}>{formatNombre(totaux?.rent ?? 0)}</td>}
              <td className={TD}>{formatNombre(totaux?.total_operating ?? 0)}</td>
              <td className={TD}>{formatNombre(totaux?.repair ?? 0)}</td>
              <td className={TD}>{formatNombre(totaux?.parts ?? 0)}</td>
              <td className={TD}>{formatNombre(totaux?.admin_papers ?? 0)}</td>
              <td className={TD}>{formatNombre(totaux?.total_maintenance ?? 0)}</td>
              {afficherAmortissement && <td className={TD}>{formatNombre(totaux?.depreciation ?? 0)}</td>}
              <td className={TD}>{formatNombre(totaux?.total_cost ?? 0)}</td>
              <td className={`${TD} ${tonDuResultat(totaux?.result)}`}>{formatNombre(totaux?.result ?? 0)}</td>
              <td className={`${TD} text-left text-xs font-normal text-muted-foreground`}>
                {totaux?.estimated ? 'Contient des montants prévus' : ''}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Aide titre="Lire ce tableau" libelle="Comment lire ce tableau">
        Montants en FCFA. Avance et Paiement sont des sommes déjà encaissées, données à titre d'information: elles ne
        s'ajoutent à aucun total. Une ligne en italique avec la mention « prévu » contient un montant estimé, pas
        encore facturé ou payé.
      </Aide>
    </div>
  );
}

// --- Acquisition du camion (amortissement) ---------------------------------------------

const vide = { cout: '', date: '', duree: '60', residuel: '0', cession: '' };

const formulaireDepuis = (actif) =>
  actif
    ? {
        cout: String(actif.acquisition_cost ?? ''),
        date: String(actif.acquisition_date || '').slice(0, 10),
        duree: String(actif.useful_life_months ?? '60'),
        residuel: String(actif.residual_value ?? '0'),
        cession: String(actif.disposal_date || '').slice(0, 10),
      }
    : vide;

/** Message de refus en français, ou `null` si la saisie est correcte. */
export const erreurActif = (form) => {
  const cout = Number(form.cout);
  const duree = Number(form.duree);
  const residuel = Number(form.residuel === '' ? 0 : form.residuel);
  if (form.cout === '' || !Number.isFinite(cout) || cout < 0) {
    return "Indiquez le coût d'acquisition du camion (zéro ou plus).";
  }
  if (!form.date) return "Indiquez la date d'acquisition du camion.";
  if (!Number.isInteger(duree) || duree < 1 || duree > 600) {
    return "La durée d'amortissement doit être comprise entre 1 et 600 mois.";
  }
  if (!Number.isFinite(residuel) || residuel < 0) return 'La valeur résiduelle ne peut pas être négative.';
  if (residuel > cout) return "La valeur résiduelle ne peut pas dépasser le coût d'acquisition.";
  if (form.cession && form.cession < form.date) return "La vente ne peut pas précéder l'acquisition.";
  return null;
};

const Ligne = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-border/50 py-1.5 text-sm last:border-0">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="text-right font-medium tabular-nums">{children}</dd>
  </div>
);

const fcfa = (valeur) => `${formatNombre(valeur)} FCFA`;

export function AcquisitionCamion({ truckId, actif, onChanged }) {
  const [form, setForm] = useState(formulaireDepuis(actif));
  const [modification, setModification] = useState(!actif);
  const [enCours, setEnCours] = useState(false);
  const [retraitDemande, setRetraitDemande] = useState(false);

  useEffect(() => {
    setForm(formulaireDepuis(actif));
    setModification(!actif);
    setRetraitDemande(false);
  }, [actif]);

  const champ = (cle) => (event) => setForm((prev) => ({ ...prev, [cle]: event.target.value }));

  const enregistrer = async (event) => {
    event.preventDefault();
    const refus = erreurActif(form);
    if (refus) {
      toast.error(refus);
      return;
    }
    setEnCours(true);
    try {
      await economicsAPI.saveTruckAsset(truckId, {
        acquisition_cost: Number(form.cout),
        acquisition_date: form.date,
        useful_life_months: Number(form.duree),
        residual_value: Number(form.residuel === '' ? 0 : form.residuel),
        disposal_date: form.cession || null,
      });
      toast.success('Acquisition du camion enregistrée');
      await onChanged?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Impossible d'enregistrer l'acquisition du camion."));
    } finally {
      setEnCours(false);
    }
  };

  const retirer = async () => {
    if (!retraitDemande) {
      setRetraitDemande(true);
      return;
    }
    setEnCours(true);
    try {
      await economicsAPI.deleteTruckAsset(truckId);
      toast.success("Acquisition du camion retirée");
      await onChanged?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Impossible de retirer l'acquisition du camion."));
    } finally {
      setEnCours(false);
      setRetraitDemande(false);
    }
  };

  return (
    <section className="space-y-3 rounded-xl border border-border bg-card p-4" data-testid="acquisition-camion">
      <div>
        <div className="flex items-center gap-1">
          <h3 className="text-base font-semibold">Acquisition du camion</h3>
          <Aide titre="Acquisition du camion">Le coût d'achat sert à calculer l'amortissement mensuel, réparti de façon égale sur la durée choisie. Il s'ajoute aux charges du camion, sans rien écrire dans la caisse.</Aide>
        </div>
      </div>

      {actif && !modification && (
        <>
          <dl className="grid gap-x-8 md:grid-cols-2">
            <Ligne label="Coût d'acquisition">{fcfa(actif.acquisition_cost)}</Ligne>
            <Ligne label="Date d'acquisition">{formatDate(actif.acquisition_date)}</Ligne>
            <Ligne label="Durée d'amortissement">{formatNombre(actif.useful_life_months)} mois</Ligne>
            <Ligne label="Valeur résiduelle">{fcfa(actif.residual_value)}</Ligne>
            <Ligne label="Amortissement par mois">{fcfa(actif.monthly_depreciation)}</Ligne>
            <Ligne label="Amortissement cumulé">{fcfa(actif.accumulated_depreciation)}</Ligne>
            <Ligne label="Valeur nette comptable">{fcfa(actif.net_book_value)}</Ligne>
            <Ligne label="Mois écoulés / restants">
              {formatNombre(actif.months_elapsed)} / {formatNombre(actif.months_remaining)}
            </Ligne>
            {actif.disposal_date && <Ligne label="Camion vendu le">{formatDate(actif.disposal_date)}</Ligne>}
          </dl>
          {actif.fully_depreciated && (
            <p className="text-sm text-muted-foreground">Ce camion est entièrement amorti.</p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setModification(true)}>
              Modifier l'acquisition
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={retirer} disabled={enCours}>
              {retraitDemande ? "Confirmer le retrait de l'acquisition" : "Retirer l'acquisition"}
            </Button>
          </div>
        </>
      )}

      {modification && (
        <form onSubmit={enregistrer} className="space-y-3" data-testid="acquisition-formulaire">
          {!actif && (
            <p className="flex items-center gap-1 rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
              Le coût d'achat de ce camion n'est pas encore saisi.
              <Aide titre="Coût d’achat">
                Sans lui, la rentabilité ne compte aucun amortissement.
              </Aide>
            </p>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="actif-cout">Coût d'acquisition (FCFA)</Label>
              <Input
                id="actif-cout"
                type="number"
                min="0"
                step="1"
                inputMode="decimal"
                value={form.cout}
                onChange={champ('cout')}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="actif-date">Date d'acquisition</Label>
              <Input id="actif-date" type="date" value={form.date} onChange={champ('date')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="actif-duree">Durée d'amortissement (mois)</Label>
              <Input
                id="actif-duree"
                type="number"
                min="1"
                max="600"
                step="1"
                inputMode="numeric"
                value={form.duree}
                onChange={champ('duree')}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="actif-residuel">Valeur résiduelle (FCFA)</Label>
              <Input
                id="actif-residuel"
                type="number"
                min="0"
                step="1"
                inputMode="decimal"
                value={form.residuel}
                onChange={champ('residuel')}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="actif-cession">Date de vente du camion (facultatif)</Label>
              <Input id="actif-cession" type="date" value={form.cession} onChange={champ('cession')} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={enCours}>
              {enCours ? 'Enregistrement…' : "Enregistrer l'acquisition"}
            </Button>
            {actif && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setForm(formulaireDepuis(actif));
                  setModification(false);
                }}
              >
                Annuler
              </Button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

// --- L'onglet ---------------------------------------------------------------------------

export default function TruckProfitTab({ truck }) {
  const navigate = useNavigate();
  const { canSeeMoney, hasModule } = useAuth();
  const truckId = truck?.id;

  const [choix, setChoix] = useState(moisCourant);
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const requete = useRef(0);

  const charger = useCallback(async () => {
    if (!truckId || !canSeeMoney) return;
    const numero = requete.current + 1;
    requete.current = numero;
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await economicsAPI.getTruckEconomics(truckId, choix);
      if (requete.current !== numero) return;
      setDonnees(reponse);
    } catch (error) {
      if (requete.current !== numero) return;
      setErreur(apiErrorMessage(error, 'Impossible de charger la rentabilité du camion.'));
    } finally {
      if (requete.current === numero) setChargement(false);
    }
  }, [truckId, canSeeMoney, choix]);

  useEffect(() => {
    charger();
  }, [charger]);
  // Fret, frais, location ou trajet modifiés ailleurs: la rentabilité suit.
  useRafraichirSur(['depenses', 'finance', 'trajets', 'locations', 'camions', 'rapports'], charger);

  if (!canSeeMoney) return null;

  const totaux = donnees?.totals || null;
  const actif = donnees?.asset || null;
  const possession = donnees?.truck?.ownership || truck?.ownership_type || 'PROPRE';
  const camionPropre = possession !== 'LOUE';
  const lignes = donnees?.lines || [];
  const rapportDisponible = typeof hasModule === 'function' ? hasModule('reports') : true;

  const imprimer = () =>
    navigate(`/rapports?camion=${encodeURIComponent(truckId)}&annee=${choix.year}&mois=${choix.month}`);

  const resultat = nombre(totaux?.result);

  return (
    <div className="space-y-4" data-testid="truck-profit-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1" role="group" aria-label="Choix du mois">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Mois précédent"
            onClick={() => setChoix((prev) => moisSuivant(prev, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[10rem] text-center text-sm font-semibold capitalize" data-testid="mois-affiche">
            {libelleMois(donnees?.period, choix)}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Mois suivant"
            disabled={estApresMoisCourant(moisSuivant(choix, 1))}
            onClick={() => setChoix((prev) => moisSuivant(prev, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        {rapportDisponible && (
          <Button type="button" variant="outline" onClick={imprimer}>
            <Printer className="mr-2 h-4 w-4" aria-hidden="true" />
            Imprimer le compte d'exploitation du camion
          </Button>
        )}
      </div>

      {chargement && !donnees && <LoadingState shape="table" />}

      {erreur && (
        <EmptyState
          variant="error"
          title="Rentabilité indisponible"
          description={erreur}
          actionLabel="Réessayer"
          onAction={charger}
        />
      )}

      {!erreur && donnees && totaux && (
        <>
          <div data-testid="cartes-rentabilite">
            <StatsCompactes
              stats={[
                {
                  label: totaux.estimated ? 'Recettes (dont prévu)' : 'Recettes',
                  valeur: nombre(totaux.total_revenue),
                  unite: 'FCFA',
                  aide: totaux.estimated ? 'Dont des montants prévus' : undefined,
                },
                { label: 'Charges', valeur: nombre(totaux.total_cost), unite: 'FCFA' },
                {
                  label: 'Résultat',
                  valeur: resultat,
                  unite: 'FCFA',
                  ton: resultat > 0 ? 'positif' : resultat < 0 ? 'critique' : 'neutre',
                },
                camionPropre
                  ? {
                      label: 'Valeur nette comptable',
                      valeur: actif ? nombre(actif.net_book_value) : '—',
                      unite: actif ? 'FCFA' : undefined,
                      aide: actif
                        ? `Amortissement du mois: ${fcfa(totaux.depreciation)}`
                        : "Prix d'achat à saisir plus bas",
                    }
                  : {
                      label: 'Fret du mois',
                      valeur: nombre(totaux.rent),
                      unite: 'FCFA',
                      aide: "Camion loué: pas d'amortissement",
                    },
              ]}
            />
          </div>

          {lignes.length === 0 ? (
            <EmptyState
              title="Aucune activité ce mois-ci"
              description="Ce camion n'a ni rotation ni charge sur ce mois. Choisissez un autre mois, ou envoyez-le charger."
            />
          ) : (
            <CompteExploitationTable lignes={lignes} totaux={totaux} />
          )}

          {camionPropre ? (
            <AcquisitionCamion truckId={truckId} actif={actif} onChanged={charger} />
          ) : (
            <p className="flex items-center gap-1 rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
              {libelle('truckOwnership', 'LOUE')}: il ne s'amortit pas.
              <Aide titre="Camion loué">Son fret figure dans les charges d'exploitation.</Aide>
            </p>
          )}
        </>
      )}
    </div>
  );
}
